import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { formatDay, todayLocal } from "@/lib/utils/date";
import {
  REMISE_NOT_RECORDED,
  RemiseButtonLabel,
  RemiseDateField,
  remiseDateError,
  type SentDocument,
  sentFormatsLabel,
} from "../components/diffusion/remise-fields";

type PendingItem = {
  transmissionId: number;
  slipId: number;
  bordereauNumber: string | null;
  createdAt: string;
  recipientTeamName: string | null;
  documents: SentDocument[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: PendingItem[] };

const LOAD_ERROR =
  "Erreur lors du chargement des diffusions prévues par courrier";

function documentsLabel(item: PendingItem): string {
  return item.documents.map((d) => d.fullname ?? "(Sans nom)").join(", ");
}

/**
 * Screen 021ter — "Remettre en main propre une diffusion prévue par courrier".
 *
 * Lists the validated bordereaux the current user sees (server rule
 * `getSlipVisibility`) that are still planned by courrier (modeEnvoi
 * "courrier", not yet sent), lets them select one or more and, after
 * confirming the mode change, records the effective
 * hand delivery. The server converts modeEnvoi from "courrier" to
 * "en_main_propre" and persists the delivery date and recording actor
 * (dateEnvoi / sentByEmployeeId) atomically and idempotently.
 *
 * Out of scope (no truthful data model yet): recipient
 * signature, delivery receipt PDF, per-document quantities.
 */
export function RemiseCourrierMainPropre() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deliveredOn, setDeliveredOn] = useState<string>(todayLocal);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const dateError = remiseDateError(deliveredOn);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is intentionally used as a refetch trigger and not referenced inside the effect body
  useEffect(() => {
    if (isNil(yardId)) return;

    let cancelled = false;

    async function fetchPending() {
      setState((prev) =>
        prev.status === "success" ? prev : { status: "loading" },
      );
      // openapi-fetch rejects on transport failures (only HTTP errors come back
      // as `error`), so guard the await or the page hangs on "Chargement...".
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/transmissions/pending-courrier",
          { params: { path: { yardId: yardId ?? "" } } },
        );

        if (cancelled) return;

        if (error || isNil(data)) {
          setState({ status: "error", error: LOAD_ERROR });
          return;
        }

        setState({ status: "success", items: data.data.items });
        setSelected(new Set());
        setConfirming(false);
      } catch {
        if (cancelled) return;
        setState({ status: "error", error: LOAD_ERROR });
      }
    }

    fetchPending();
    return () => {
      cancelled = true;
    };
  }, [yardId, reloadKey]);

  const items = state.status === "success" ? state.items : [];
  const allSelected = items.length > 0 && selected.size === items.length;

  const selectedIds = useMemo(() => [...selected], [selected]);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setConfirming(false);
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === items.length
        ? new Set()
        : new Set(items.map((i) => i.transmissionId)),
    );
    setConfirming(false);
  }

  async function submitDelivery() {
    if (isNil(yardId) || selectedIds.length === 0) return;
    setSubmitting(true);

    // Only back-date when the user picked a day other than today; today lets
    // the server stamp the exact moment (now) and dodges timezone-midnight
    // rounding that could look like a future date to the API guard.
    const deliveredAt =
      deliveredOn === todayLocal()
        ? undefined
        : new Date(`${deliveredOn}T12:00:00`).toISOString();

    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/transmissions/deliver-courrier-in-person",
        {
          params: { path: { yardId } },
          body: { transmissionIds: selectedIds, deliveredAt },
        },
      );

      if (error || isNil(data)) {
        toaster.error({
          title: "Remise non enregistrée",
          description: error?.error ?? REMISE_NOT_RECORDED,
        });
        return;
      }

      const count = data.data.deliveredIds.length;
      toaster.success({
        title: "Remise enregistrée",
        description:
          count > 1
            ? `${count} bordereaux remis en main propre`
            : "1 bordereau remis en main propre",
      });
      setConfirming(false);
      setReloadKey((k) => k + 1);
    } catch {
      toaster.error({
        title: "Remise non enregistrée",
        description: REMISE_NOT_RECORDED,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const total = items.length;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-end justify-between gap-4 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Remettre en main propre une diffusion prévue par courrier
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {total} bordereau{total > 1 ? "x" : ""} prévu
              {total > 1 ? "s" : ""} par courrier
            </p>
          )}
        </div>
        <div className="flex items-end gap-3">
          <RemiseDateField
            value={deliveredOn}
            onChange={setDeliveredOn}
            error={dateError}
          />
          <button
            type="button"
            disabled={
              selectedIds.length === 0 || submitting || isNotNil(dateError)
            }
            onClick={() => setConfirming(true)}
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-zinc-300 dark:disabled:bg-zinc-700"
          >
            <RemiseButtonLabel
              label="Remettre en main propre"
              count={selectedIds.length}
            />
          </button>
        </div>
      </div>

      {confirming && selectedIds.length > 0 && (
        <div className="flex items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {selectedIds.length > 1
              ? `Ces ${selectedIds.length} bordereaux étaient prévus par courrier. Confirmer leur remise en main propre ?`
              : "Ce bordereau était prévu par courrier. Confirmer sa remise en main propre ?"}
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => setConfirming(false)}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:cursor-not-allowed dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={submitting || isNotNil(dateError)}
              onClick={submitDelivery}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-zinc-300 dark:disabled:bg-zinc-700"
            >
              {submitting ? "Enregistrement..." : "Confirmer la remise"}
            </button>
          </div>
        </div>
      )}

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {/*
              Screen-design rules: fixed table layout, Documents column takes
              the remaining width, fixed row height with truncation (full text
              on hover), sticky header, only the body scrolls vertically.
            */}
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-10" />
                <col className="w-32" />
                <col className="w-36" />
                <col className="w-48" />
                <col />
                <col className="w-40" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner"
                      checked={allSelected}
                      disabled={items.length === 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Bordereau
                  </th>
                  <th
                    className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300"
                    title="Date de création"
                  >
                    Date de création
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Destinataire
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Documents
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Formats
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-sm text-zinc-500"
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-12 text-center text-sm text-zinc-500"
                    >
                      Aucune diffusion prévue par courrier à remettre en main
                      propre
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const documents = documentsLabel(item);
                    const formats = sentFormatsLabel(item.documents);
                    const checked = selected.has(item.transmissionId);
                    return (
                      <tr
                        key={item.transmissionId}
                        className="h-10 border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                      >
                        <td className="px-3 text-center">
                          <input
                            type="checkbox"
                            aria-label={`Sélectionner ${
                              item.bordereauNumber ?? "bordereau"
                            }`}
                            checked={checked}
                            onChange={() => toggle(item.transmissionId)}
                          />
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={item.bordereauNumber ?? undefined}>
                            {item.bordereauNumber ?? "(Sans numéro)"}
                          </span>
                        </td>
                        <td className="truncate px-3 text-left">
                          {formatDay(item.createdAt)}
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={item.recipientTeamName ?? undefined}>
                            {item.recipientTeamName ?? "-"}
                          </span>
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={documents || undefined}>
                            {documents || "-"}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "truncate whitespace-nowrap px-3 text-center",
                            formats.length === 0 && "text-zinc-300",
                          )}
                          title={formats || undefined}
                        >
                          {formats || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
